/**
 * WHERE a server refusal is shown — the pure half of the decision, so it can be tested without a DOM.
 *
 * The bug this exists to prevent (round-4 review): the duplicate-session message was written with `showStatus`,
 * which paints `#status` INSIDE the menu — and `hideMenu()` removes the menu from the DOM as soon as the sea
 * starts. A bumped tab is welcomed by definition, so the one message it actually needed was a silent no-op and all
 * the captain saw was a reconnect spinner. Anything terminal that can arrive mid-game must go to a permanent HUD
 * element instead.
 *
 * `menu` → the menu's status line (pre-welcome; SET SAIL is right there as the retry).
 * `hud`  → the permanent HUD banner (in-game; the menu is gone, so the action is a reload).
 */
import { REJECT } from '@piratas/protocol';

export type RejectChannel = 'menu' | 'hud';
export interface RejectPresentation {
  channel: RejectChannel;
  text: string;
  /** "The sea is full" keeps its own friendly copy + leaves SET SAIL armed as the retry. */
  seaFull: boolean;
}

/** Is this one of the session-guard refusals the client renders as guidance rather than as an error? */
export function isSessionReject(reason: string): boolean {
  return reason === REJECT.DUPLICATE_SESSION || reason === REJECT.IDENTITY_BUSY;
}

/**
 * Decide how to present an S_REJECT. `welcomed` = this connection had completed a handshake at least once, i.e.
 * the captain is in the world and the menu no longer exists.
 */
export function rejectPresentation(reason: string, welcomed: boolean): RejectPresentation {
  if (/sea is full/i.test(reason)) {
    return { channel: 'menu', text: '⚓ The sea is full — tap SET SAIL to try again in a moment.', seaFull: true };
  }
  // A session refusal is shown as the server phrased it (the wording already carries the instruction) — no
  // "Rejected:" scolding for what is almost always a stale tab.
  if (isSessionReject(reason)) {
    return welcomed ? { channel: 'hud', text: reason, seaFull: false } : { channel: 'menu', text: `⚓ ${reason}.`, seaFull: false };
  }
  // Everything else (banned, kicked, join failed…) — terminal too, and mid-game it must still be visible.
  return welcomed ? { channel: 'hud', text: reason, seaFull: false } : { channel: 'menu', text: `Rejected: ${reason}`, seaFull: false };
}
